import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Clock, CheckCircle, FileText, RefreshCw, Package, Briefcase } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import KpiCard from '@/features/rh/components/dashboard/KpiCard';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { config } from '@/config/env';
import { Revelar } from '@/shared/components/Revelar';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface Supplier {
  id: string;
  legalName: string;
  status: string;
  createdAt: string;
}

interface InternalSuppliersResponse {
  data: Supplier[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface SupplierMetrics {
  total: number;
  approved: number;
  inReview: number;
  drafts: number;
  rejected: number;
  pendingPurchases: number;
  pendingControlling: number;
  pendingInfo: number;
}

const STATUS_LABELS: Record<string, string> = {
  approved: 'Aprobados',
  sent_to_review: 'En revisión',
  pending_purchases: 'Pend. compras',
  pending_controlling: 'Pend. controlling',
  pending_info: 'Pend. información',
  rejected: 'Rechazados',
  draft: 'Solicitudes',
};

const PIE_COLORS = ['#4ADE80', '#6366F1', '#F59E0B', '#8B5CF6', '#60A5FA', '#F87171', '#94A3B8'];

const ProveedoresDashboard = () => {
  const { user } = useAuth();
  const permissions = user?.rbac?.effectivePermissions || [];
  const has = (permission: string) => userHasPermission(permission, permissions);

  const hasDashboardAccess = has('proveedores.inicio.dashboard.consultar');
  const canViewProveedores = has('proveedores.administracion.proveedores.consultar');
  const canViewOrdenes = has('proveedores.administracion.ordenes_compra.consultar');
  // Acciones rápidas se muestra junto a la gráfica de distribución (columna
  // derecha) solo si hay al menos una acción disponible.
  const showActions = canViewProveedores || canViewOrdenes;

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<SupplierMetrics>({
    total: 0,
    approved: 0,
    inReview: 0,
    drafts: 0,
    rejected: 0,
    pendingPurchases: 0,
    pendingControlling: 0,
    pendingInfo: 0,
  });

  const loadMetrics = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const fetchSupplierCount = async (statuses: string[]) => {
        const params = new URLSearchParams({
          page: '1',
          limit: '1',
          status: statuses.join(','),
        });

        const response = await fetch(`${config.apiBaseUrl}/internal/suppliers?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load supplier metrics for statuses: ${statuses.join(', ')}`);
        }

        const payload: InternalSuppliersResponse = await response.json();
        return payload.total ?? 0;
      };

      const [
        approved,
        sentToReview,
        pendingPurchases,
        pendingControlling,
        rejected,
        drafts,
      ] = await Promise.all([
        fetchSupplierCount(['approved']),
        fetchSupplierCount(['sent_to_review']),
        fetchSupplierCount(['pending_purchases']),
        fetchSupplierCount(['pending_controlling']),
        fetchSupplierCount(['rejected']),
        fetchSupplierCount(['pre_registered']),
      ]);

      const inReview =
        sentToReview + pendingPurchases + pendingControlling;
      const total =
        approved + inReview + rejected + drafts;

      setMetrics({
        total,
        approved,
        inReview,
        drafts,
        rejected,
        pendingPurchases,
        pendingControlling,
        pendingInfo: 0,
      });
    } catch (err) {
      console.error('Error loading supplier metrics:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  if (!hasDashboardAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  const statusChartData = [
    { name: 'Aprobados', value: metrics.approved },
    { name: 'En revisión', value: metrics.inReview },
    { name: 'Rechazados', value: metrics.rejected },
    { name: 'Solicitudes', value: metrics.drafts },
  ].filter(item => item.value > 0);

  const detailedStatusData = [
    { name: STATUS_LABELS.approved, value: metrics.approved },
    { name: STATUS_LABELS.sent_to_review, value: metrics.inReview - metrics.pendingPurchases - metrics.pendingControlling - metrics.pendingInfo },
    { name: STATUS_LABELS.pending_purchases, value: metrics.pendingPurchases },
    { name: STATUS_LABELS.pending_controlling, value: metrics.pendingControlling },
    { name: STATUS_LABELS.pending_info, value: metrics.pendingInfo },
    { name: STATUS_LABELS.rejected, value: metrics.rejected },
    { name: STATUS_LABELS.draft, value: metrics.drafts },
  ].filter(item => item.value > 0);

  return (
    <div className="mx-auto ml-2 px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">
              Monitoreo y gestión de proveedores
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <button
              onClick={loadMetrics}
              disabled={isLoading}
              className={estiloDeBoton('secundario')}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'girando' : ''}`} />
              <span>{isLoading ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KpiCard
          title="Total proveedores"
          value={metrics.total}
          icon={<Users />}
          variant="purple"
          isLoading={isLoading}
        />
        <KpiCard
          title="Proveedores aprobados"
          value={metrics.approved}
          icon={<CheckCircle />}
          variant="success"
          isLoading={isLoading}
        />
        <KpiCard
          title="En revisión"
          value={metrics.inReview}
          icon={<Clock />}
          variant="info"
          isLoading={isLoading}
        />
        <KpiCard
          title="Solicitudes"
          value={metrics.drafts}
          icon={<FileText />}
          variant="warning"
          isLoading={isLoading}
        />
      </div>

      {/* Fila 1: Distribución (izq) | Acciones rápidas (der, estilo Almacén) */}
      <div className={`grid grid-cols-1 gap-6 mb-8 ${showActions ? 'lg:grid-cols-2' : ''}`}>
        {/* Distribution Pie Chart */}
        <Revelar>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Distribución de proveedores</h3>
          {statusChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusChartData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {statusChartData.map((item, index) => (
                  <div key={item.name} className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}></div>
                    <span className="text-sm text-gray-700">{item.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
              Sin datos para mostrar
            </div>
          )}
        </div>
        </Revelar>

        {/* Quick Actions — estilo homologado con el dashboard de Almacén */}
        {showActions && (
          <Revelar>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Acciones rápidas</h3>
            <div className="space-y-3">
              {canViewProveedores && (
                <Link
                  to="/super-admin-proveedores"
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-all text-left"
                  style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E0E7F5' }}>
                    <Users className="w-5 h-5" style={{ color: '#315FB9' }} />
                  </div>
                  <span className="text-sm font-medium text-gray-800">Ver proveedores</span>
                </Link>
              )}

              {canViewProveedores && (
                <Link
                  to="/solicitudes-proveedores"
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-all text-left"
                  style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E0E7F5' }}>
                    <Briefcase className="w-5 h-5" style={{ color: '#315FB9' }} />
                  </div>
                  <span className="text-sm font-medium text-gray-800">Gestionar solicitudes</span>
                </Link>
              )}

              {canViewOrdenes && (
                <Link
                  to="/rh/purchase-orders"
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-all text-left"
                  style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E0E7F5' }}>
                    <Package className="w-5 h-5" style={{ color: '#315FB9' }} />
                  </div>
                  <span className="text-sm font-medium text-gray-800">Órdenes de compra</span>
                </Link>
              )}
            </div>
          </div>
          </Revelar>
        )}
      </div>

      {/* Fila 2: Proveedores por estatus (barra) a todo lo ancho */}
      <Revelar className="mb-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Proveedores por estatus</h3>
        {detailedStatusData.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={detailedStatusData}
              margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
            >
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                interval={0}
                angle={0}
              />
              <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {detailedStatusData.map((_item, index) => (
                  <Cell key={`bar-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
            Sin datos para mostrar
          </div>
        )}
      </div>
      </Revelar>
    </div>
  );
};

export default ProveedoresDashboard;
